import React from 'react'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import { Box, Tooltip } from '@mui/material'
import { styled } from '@mui/material/styles'
import Html from '../../components/Html/Html'
import { useSideNavigationContext } from '../../utils/context/sideNavigationContext'
import {
  formatDate,
  formatDollarsWithSymbol,
  formatMillion,
  formatPercentage,
} from '../../utils/functions'
import {
  BLUE_BASE,
  ERROR_MAIN,
  GREEN,
  WHITE,
} from '../../theme/variables/colors'

// Styled Components (unchanged)
const StyledSection = styled(Box)(({ theme }) => ({
  backgroundColor: BLUE_BASE,
  padding: '20px',
  color: WHITE,
}))

const DataRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  gap: '20px',
  padding: '0 20px',
}))

const DataPoint = styled(Box)(({ theme }) => ({
  marginBottom: 0,
}))

const Label = styled(Box)(({ theme }) => ({
  fontSize: '14px',
  color: WHITE,
  display: 'flex',
  alignItems: 'center',
}))

const Value = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'showOnlyDesktop' && prop !== 'sendColor',
})(({ theme, sendColor }) => ({
  fontSize: '20px',
  color: sendColor || WHITE,
  fontWeight: 'bold',
  marginTop: '5px',
}))

const CustomTooltip = styled(Tooltip)(({ theme }) => ({
  tooltip: {
    backgroundColor: WHITE,
    color: '#000',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
    padding: '10px',
  },
  arrow: {
    color: WHITE,
  },
}))

const infoIconStyles = {
  fontSize: '16px',
  marginLeft: '4px',
  verticalAlign: 'middle',
  cursor: 'pointer',
}

// Helper Function
const safeFormat = (value, formatter) => {
  if (typeof value === 'number' && !isNaN(value)) {
    return typeof formatter === 'function' ? formatter(value) : value
  } else {
    return '--'
  }
}

function FundSnapshot({ overview_section, pageContext, fund_classes }) {
  const { createSectionRef } = useSideNavigationContext()

  const fundClass = fund_classes?.find(
    (item) => item?.share_class[0]?.title === pageContext.fundClass?.title
  )
  if (!fundClass) return null

  const isETF =
    fundClass?.share_class?.[0]?.product_type?.toLowerCase() === 'etf'

  const priceData =
    overview_section?.inline_section?.[0]?.api_reference?.[0]?.data?.price || {}
  const valuationData = isETF
    ? overview_section?.inline_section?.[1]?.api_reference?.[0]?.data
        ?.etfNavBasket || {}
    : overview_section?.inline_section?.[1]?.api_reference?.[0]?.data
        ?.valuation || {}
  const compositeValuationData =
    overview_section?.inline_section?.[1]?.api_reference?.[0]?.data
      ?.fundComposite?.compositeValuation || {}

  const grossExpenseRatio = fundClass?.expense_ratio?.gross
  const netExpenseRatio = fundClass?.expense_ratio?.net
  const netExpenseRatioPopup = fundClass?.expense_ratio?.net_popup || []
  const benchmark = isETF
    ? fundClass?.etf_product_facts?.[0]?.benchmark || '--'
    : '--'

  const priceNav = priceData?.nav
  const navChangeDollars = priceData?.navChangeDollars
  const navChangePercent = priceData?.navChangePercent
  const navYtdReturn = priceData?.ytdReturn
  const totalFundNetAssets = compositeValuationData?.tna
  const totalShareClassNetAssets = valuationData?.tna

  const asOfDatePrice = priceData?.calendar?.date
    ? formatDate(priceData.calendar.date)
    : '--'

  const asOfDateAssets = valuationData?.calendar?.date
    ? formatDate(valuationData.calendar.date)
    : '--'

  const getColor = (value) => {
    if (typeof value === 'number') {
      if (value > 0) {
        return GREEN
      }
      if (value < 0) {
        return ERROR_MAIN
      }
    }
    return WHITE
  }

  function createPopover(popup) {
    if (!popup || !Array.isArray(popup)) {
      return null
    }
    if (!popup[0]?.html || !Array.isArray(popup[0].html)) {
      return null
    }
    return (
      <Box>
        {popup[0].html.map((htmlContent, index) => (
          <Box key={`expense-ratio-popup-${index}`}>
            <Html content={htmlContent} nowrap={true} />
          </Box>
        ))}
      </Box>
    )
  }

  const navChangePercentColor = getColor(navChangePercent)
  const navYtdReturnColor = getColor(navYtdReturn)

  return (
    <StyledSection ref={createSectionRef(0)}>
      <DataRow>
        {/* NAV as of Date */}
        <DataPoint>
          <Label>NAV as of {asOfDatePrice}</Label>
          <Value>{safeFormat(priceNav, formatDollarsWithSymbol)}</Value>
        </DataPoint>

        <DataPoint>
          <Label>NAV Daily Change</Label>
          <Value sendColor={navChangePercentColor}>
            {safeFormat(navChangeDollars, formatDollarsWithSymbol)}{' '}
            {typeof navChangePercent === 'number'
              ? navChangePercent > 0
                ? '↑'
                : navChangePercent < 0
                  ? '↓'
                  : ''
              : ''}
          </Value>
        </DataPoint>

        <DataPoint>
          <Label>NAV YTD Return</Label>
          <Value sendColor={navYtdReturnColor}>
            {safeFormat(navYtdReturn, formatPercentage)}{' '}
            {typeof navYtdReturn === 'number'
              ? navYtdReturn > 0
                ? '↑'
                : navYtdReturn < 0
                  ? '↓'
                  : ''
              : ''}
          </Value>
        </DataPoint>

        <DataPoint>
          <Label>
            Net Expense Ratio (%)
            <CustomTooltip
              title={createPopover(netExpenseRatioPopup)}
              placement="top"
              arrow
            >
              <InfoIcon style={infoIconStyles} />
            </CustomTooltip>
          </Label>
          <Value>
            {typeof netExpenseRatio === 'number' ||
            ('string' && !isNaN(netExpenseRatio))
              ? netExpenseRatio
              : '--'}
          </Value>
        </DataPoint>

        <DataPoint>
          <Label>Total Net Assets (million) as of {asOfDateAssets}</Label>
          <Value>
            ${safeFormat(totalFundNetAssets, (val) => formatMillion(val, 3))}
          </Value>
        </DataPoint>

        {!isETF && (
          <DataPoint>
            <Label>Total Share Class Net Assets (million)</Label>
            <Value>
              $
              {safeFormat(totalShareClassNetAssets, (val) =>
                formatMillion(val, 3)
              )}
            </Value>
          </DataPoint>
        )}

        {isETF && benchmark && (
          <DataPoint>
            <Label>Benchmark</Label>
            <Value>{benchmark}</Value>
          </DataPoint>
        )}
      </DataRow>
    </StyledSection>
  )
}

export default FundSnapshot